import { useEffect, useState } from "react";
import { siteSessionToken } from "@/shared/api/adminHeaders";
import { BACKEND_API_URL } from "@/shared/config/app";

/**
 * Нужен ли человеку план тренировок — GET /api/plan-reminder/me (site_server, свой сеанс:
 * site_session_token в Authorization: Bearer). Ответ {needsPlan, state}: плана нет, он пустой или
 * кончился — needsPlan. Сервер считает это так же, как ночное напоминание.
 *
 * Не вошёл, сеть, сбой сервера — null: подсказку не показываем (лишней лучше не быть).
 * Ответ помнится минуту на сеанс: профиль и «История баллов» в одном заходе не спрашивают дважды.
 */
export const planReminderUrl = (): string => `${BACKEND_API_URL}/api/plan-reminder/me`;

const TTL_MS = 60_000;
const TIMEOUT_MS = 8_000;

let memo: { token: string; at: number; value: boolean | null } | null = null;
let inflight: { token: string; p: Promise<boolean | null> } | null = null;

/** Для тестов и выхода из аккаунта. */
export const resetNeedsPlanMemo = (): void => {
    memo = null;
    inflight = null;
};

const ask = async (token: string): Promise<boolean | null> => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    try {
        const res = await fetch(planReminderUrl(), {
            headers: { Authorization: `Bearer ${token}` },
            signal: ctrl.signal,
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { needsPlan?: unknown } | null;
        return typeof body?.needsPlan === "boolean" ? body.needsPlan : null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

export const fetchNeedsPlan = async (): Promise<boolean | null> => {
    const token = siteSessionToken();
    if (!token) return null;
    if (memo && memo.token === token && Date.now() - memo.at < TTL_MS) return memo.value;
    if (inflight && inflight.token === token) return inflight.p;
    const p = ask(token).then((value) => {
        memo = { token, at: Date.now(), value };
        if (inflight?.p === p) inflight = null;
        return value;
    });
    inflight = { token, p };
    return p;
};

/** true — показать подсказку; до ответа и при любом сбое — false. `enabled` — только свой вид. */
export const useNeedsPlan = (enabled: boolean): boolean => {
    const [needs, setNeeds] = useState(false);
    useEffect(() => {
        if (!enabled) {
            setNeeds(false);
            return;
        }
        let alive = true;
        void fetchNeedsPlan().then((v) => {
            if (alive) setNeeds(v === true);
        });
        return () => {
            alive = false;
        };
    }, [enabled]);
    return needs;
};
